/**
 * RestoreSaveDialog Component
 * 
 * Shown when a player returns and has a saved mid-episode state.
 * Offers the choice to continue from where they left off or start fresh.
 */

import type { EpisodeSaveState } from '../utils/sessionPersistence';

interface RestoreSaveDialogProps {
  savedState: EpisodeSaveState;
  onRestore: () => void;
  onStartFresh: () => void;
  isClearing?: boolean;
}

export default function RestoreSaveDialog({
  savedState,
  onRestore,
  onStartFresh,
  isClearing = false,
}: RestoreSaveDialogProps) {
  const savedDate = new Date(savedState.savedAt);
  const formattedDate = savedDate.toLocaleDateString('pt-PT', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });

  const messageCount = savedState.transcript.length;
  const durationMinutes = Math.round(savedState.duration / 60000);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-[#FFFBF5] rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-accent px-6 py-5 text-white">
          <div className="flex items-center gap-3">
            <div className="text-3xl">💾</div>
            <div>
              <h2 className="text-xl font-bold">Progresso Guardado</h2>
              <p className="text-white/80 text-sm mt-0.5">
                Tens uma conversa guardada
              </p>
            </div>
          </div>
        </div>

        {/* Save details */}
        <div className="px-6 py-4">
          <div className="bg-white rounded-xl p-4 space-y-2 border border-gray-100">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Personagem</span>
              <span className="font-medium text-dark">
                {savedState.scenarioData.characterName || savedState.scenarioData.aiRole}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Mensagens</span>
              <span className="font-medium text-dark">{messageCount}</span>
            </div>
            {durationMinutes > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Duração</span>
                <span className="font-medium text-dark">{durationMinutes} min</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Guardado em</span>
              <span className="font-medium text-dark">{formattedDate}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 space-y-3">
          <button
            onClick={onRestore}
            className="w-full px-6 py-3.5 bg-gradient-to-r from-primary to-accent hover:opacity-90 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.01]"
          >
            Continuar de onde parei
          </button>
          <button
            onClick={onStartFresh}
            disabled={isClearing}
            className="w-full px-6 py-3 bg-white hover:bg-gray-50 disabled:opacity-60 text-gray-600 font-medium rounded-xl border border-gray-200 transition-colors"
          >
            {isClearing ? 'A limpar...' : 'Começar de novo'}
          </button>
        </div>
      </div>
    </div>
  );
}
